import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCheck } from 'react-icons/fi';
import { apiClient } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Cache to track users who have a confirmed talent profile
// This prevents redundant "Create Profile" API calls for subsequent additions
const verifiedProfiles = new Set<string>();

interface AddSkillModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const PROFICIENCY_LEVELS = [
    { level: 1, label: 'Beginner', description: 'Basic understanding, limited experience' },
    { level: 2, label: 'Novice', description: 'Some experience, can work with guidance' },
    { level: 3, label: 'Intermediate', description: 'Solid experience, can work independently' },
    { level: 4, label: 'Advanced', description: 'Extensive experience, can mentor others' },
    { level: 5, label: 'Expert', description: 'Deep expertise, thought leader in the field' },
];

const SKILL_CATEGORIES = [
    { id: 'professional', name: 'Professional' },
    { id: 'practical', name: 'Practical' },
    { id: 'soft', name: 'Soft Skills' },
    { id: 'physical', name: 'Physical' },
    { id: 'frontend', name: 'Frontend' },
    { id: 'backend', name: 'Backend' },
    { id: 'ai', name: 'AI/ML' },
    { id: 'general', name: 'General' },
];

const AddSkillModal: React.FC<AddSkillModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Form State
    const [name, setName] = useState('');
    const [category, setCategory] = useState('general');
    const [proficiency, setProficiency] = useState(3);
    const [yearsOfExperience, setYearsOfExperience] = useState(1);
    const [description, setDescription] = useState('');
    const [addAnother, setAddAnother] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) return;

        setLoading(true);
        setError('');

        try {
            const skillData = {
                name,
                category,
                description,
                proficiency,
                yearsOfExperience,
                lastUsed: new Date().toISOString(),
            };

            // Ensure talent profile exists before adding skill
            if (!verifiedProfiles.has(user.id)) {
                try {
                    await apiClient.createTalentProfile(user.id, {
                        name: `${user.firstName} ${user.lastName}`,
                        headline: 'Software Developer',
                        bio: '',
                        location: '',
                        status: 'active',
                        skills: []
                    });
                    verifiedProfiles.add(user.id);
                } catch (profileErr: any) {
                    // If conflict (already exists), mark as verified
                    if (profileErr.status === 409 || (profileErr.message && profileErr.message.includes('exists'))) {
                        verifiedProfiles.add(user.id);
                    }
                    console.log('Talent profile check/create:', profileErr.message);
                }
            }

            await apiClient.addUserSkill(user.id, skillData);

            if (addAnother) {
                onSuccess(); // Refresh the list
                // Reset form but keep modal open
                setName('');
                setCategory('general');
                setProficiency(3);
                setYearsOfExperience(1);
                setDescription('');
                setError(''); // Clear any previous errors
            } else {
                onSuccess();
                onClose();
                // Reset form
                setName('');
                setCategory('general');
                setProficiency(3);
                setYearsOfExperience(1);
                setDescription('');
            }
            // Reset form
            setName('');
            setCategory('general');
            setProficiency(3);
            setYearsOfExperience(1);
            setDescription('');
        } catch (err: any) {
            console.error('Failed to add skill:', err);
            setError(err.message || 'Failed to add skill. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                    />

                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 20 }}
                        className="relative w-full max-w-lg max-h-[90vh] bg-[#1a1510] border border-premium-gold/30 rounded-xl shadow-[0_0_50px_rgba(212,175,55,0.1)] overflow-hidden flex flex-col"
                    >
                        {/* Header - Fixed */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-premium-gold/20 bg-[#241e16] flex-shrink-0">
                            <h2 className="font-serif text-2xl text-premium-gold">Add New Skill</h2>
                            <button
                                onClick={onClose}
                                className="text-white/50 hover:text-white transition-colors"
                            >
                                <FiX size={24} />
                            </button>
                        </div>

                        {/* Body - Scrollable */}
                        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1 custom-scrollbar">
                            {error && (
                                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded text-red-200 text-sm">
                                    {error}
                                </div>
                            )}

                            {/* Name */}
                            <div>
                                <label className="block text-sm font-medium text-premium-gold/80 mb-2">
                                    Skill Name
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g. React Native, Project Management"
                                    className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-premium-gold/50 focus:ring-1 focus:ring-premium-gold/50 transition-all"
                                />
                            </div>

                            {/* Category & Experience */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-premium-gold/80 mb-2">
                                        Category
                                    </label>
                                    <select
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value)}
                                        className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-premium-gold/50 transition-all appearance-none"
                                    >
                                        {SKILL_CATEGORIES.map(cat => (
                                            <option key={cat.id} value={cat.id} className="bg-[#1a1510]">
                                                {cat.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-premium-gold/80 mb-2">
                                        Years of Experience
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="50"
                                        value={yearsOfExperience}
                                        onChange={(e) => {
                                            const val = parseInt(e.target.value);
                                            setYearsOfExperience(isNaN(val) ? 1 : val);
                                        }}
                                        className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-premium-gold/50 transition-all"
                                    />
                                </div>
                            </div>

                            {/* Proficiency */}
                            <div>
                                <label className="block text-sm font-medium text-premium-gold/80 mb-2">
                                    Proficiency Level: <span className="text-white ml-2 property-value">{PROFICIENCY_LEVELS[proficiency - 1].label}</span>
                                </label>
                                <div className="flex gap-2 mb-2">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            type="button"
                                            onClick={() => setProficiency(star)}
                                            className={`text-2xl transition-all hover:scale-110 ${star <= proficiency ? 'text-premium-gold' : 'text-white/20'
                                                }`}
                                        >
                                            ★
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs text-white/50">
                                    {PROFICIENCY_LEVELS[proficiency - 1].description}
                                </p>
                            </div>

                            {/* Description (Optional) */}
                            <div>
                                <label className="block text-sm font-medium text-premium-gold/80 mb-2">
                                    Description / Context (Optional)
                                </label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Briefly describe how you use this skill..."
                                    rows={3}
                                    className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-premium-gold/50 transition-all resize-none"
                                />
                            </div>

                            <div className="flex items-center pt-2">
                                <label className="flex items-center gap-2 cursor-pointer text-sm text-premium-gold/80 hover:text-premium-gold transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={addAnother}
                                        onChange={(e) => setAddAnother(e.target.checked)}
                                        className="w-4 h-4 rounded border-premium-gold/30 bg-black/30 text-premium-gold focus:ring-premium-gold/50"
                                    />
                                    Add another skill
                                </label>
                            </div>

                            {/* Actions */}
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-6 py-2 text-white/70 hover:text-white transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-8 py-2 bg-gradient-to-r from-premium-gold-dark to-premium-gold text-[#1a1510] font-bold rounded-lg hover:shadow-[0_0_15px_rgba(212,175,55,0.4)] transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:pointer-events-none flex items-center gap-2"
                                >
                                    {loading ? 'Adding...' : (
                                        <>
                                            <FiCheck /> Add Skill
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default AddSkillModal;
